import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminNavbar.css";

/* Highlight search match in text */
function highlight(text, query) {
  if (!query || !text) return text || "—";
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
}

const EMPTY_FORM = {
  label:     "",
  link:      "",
  parent_id: "",
  order:     "0",
  active:    1,
};

/* ─────────────────────────────────
   Dot Menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="anb-dot-menu" ref={ref}>
      <button
        className="anb-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="anb-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="anb-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="anb-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="anb-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="anb-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status Badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`anb-badge ${isActive ? "anb-badge--active" : "anb-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ item, allItems, onClose }) {
  if (!item) return null;

  const isParent = !item.parent_id;
  const children = isParent
    ? allItems.filter((i) => i.parent_id === item.id)
    : [];
  const parentItem = !isParent
    ? allItems.find((i) => i.id === item.parent_id)
    : null;

  return (
    <div className="anb-modal-overlay" onClick={onClose}>
      <div className="anb-modal anb-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="anb-modal__header">
          <div className="anb-modal__title-row">
            <span className="anb-modal__tag">VIEW</span>
            <h2 className="anb-modal__title">{item.label || "Nav Item"}</h2>
          </div>
          <button className="anb-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="anb-modal__body anb-modal__body--view">
          <div className="anb-view-grid">
            <div className="anb-view-row">
              <span className="anb-view-row__label">Label</span>
              <span className="anb-view-row__val">{item.label || "—"}</span>
            </div>
            <div className="anb-view-row">
              <span className="anb-view-row__label">Type</span>
              <span className="anb-view-row__val">
                {isParent
                  ? <span className="anb-type-badge anb-type-badge--parent">Parent</span>
                  : <span className="anb-type-badge anb-type-badge--child">Child</span>}
              </span>
            </div>
            <div className="anb-view-row anb-view-row--full">
              <span className="anb-view-row__label">Link / URL</span>
              <span className="anb-view-row__val">
                {item.link
                  ? <a href={item.link} target="_blank" rel="noreferrer">{item.link}</a>
                  : "—"}
              </span>
            </div>
            <div className="anb-view-row">
              <span className="anb-view-row__label">Order</span>
              <span className="anb-view-row__val">{item.order ?? "—"}</span>
            </div>
            <div className="anb-view-row">
              <span className="anb-view-row__label">Status</span>
              <span className="anb-view-row__val">
                <StatusBadge active={item.active} />
              </span>
            </div>
            {parentItem && (
              <div className="anb-view-row anb-view-row--full">
                <span className="anb-view-row__label">Parent</span>
                <span className="anb-view-row__val">{parentItem.label}</span>
              </div>
            )}
            <div className="anb-view-row">
              <span className="anb-view-row__label">Created</span>
              <span className="anb-view-row__val">
                {item.created_at
                  ? new Date(item.created_at).toLocaleDateString("en-ZA")
                  : "—"}
              </span>
            </div>
            <div className="anb-view-row">
              <span className="anb-view-row__label">Updated</span>
              <span className="anb-view-row__val">
                {item.updated_at
                  ? new Date(item.updated_at).toLocaleDateString("en-ZA")
                  : "—"}
              </span>
            </div>
          </div>

          {isParent && children.length > 0 && (
            <div className="anb-view-children">
              <h4 className="anb-view-children__title">
                Dropdown Children ({children.length})
              </h4>
              {children.map((child) => (
                <div key={child.id} className="anb-view-child-row">
                  <span className="anb-view-child-row__label">{child.label}</span>
                  <span className="anb-view-child-row__link">{child.link || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   FORM MODAL (Create / Edit)
   ═══════════════════════════════════ */
function FormModal({ mode, item, allItems, onClose, onSaved }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  /* Parents available to pick (exclude self and any item whose parent is self) */
  const parentOptions = allItems.filter(
    (i) => !i.parent_id && (mode === "create" || i.id !== item?.id)
  );

  useEffect(() => {
    if (mode === "edit" && item) {
      setForm({
        label:     item.label     || "",
        link:      item.link      || "",
        parent_id: item.parent_id != null ? String(item.parent_id) : "",
        order:     item.order     ?? "0",
        active:    Number(item.active) === 1 ? 1 : 0,
      });
    }
  }, [mode, item]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    setError("");
    if (!form.label.trim()) return setError("Label is required.");
    if (!form.link.trim())  return setError("Link / URL is required.");

    setSaving(true);
    try {
      const payload = {
        label:     form.label.trim(),
        link:      form.link.trim(),
        parent_id: form.parent_id !== "" ? parseInt(form.parent_id) : null,
        order:     parseInt(form.order) || 0,
        active:    form.active,
      };

      if (mode === "create") {
        await apiFetch("/api/admin/navbar", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/api/admin/navbar/${item.id}`, {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isChild = form.parent_id !== "";

  return (
    <div className="anb-modal-overlay" onClick={onClose}>
      <div className="anb-modal anb-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="anb-modal__header">
          <div className="anb-modal__title-row">
            <span className="anb-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="anb-modal__title">
              {mode === "create" ? "Add Nav Item" : "Edit Nav Item"}
            </h2>
          </div>
          <button className="anb-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="anb-modal__body anb-modal__body--form">
          {error && <div className="anb-modal__error">⚠ {error}</div>}

          {/* ── Section: Item Details ── */}
          <div className="anb-form-section">
            <h3 className="anb-form-section__title">Item Details</h3>
            <div className="anb-form-grid">
              <div className="anb-form-field anb-form-field--full">
                <label>Label <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.label}
                  onChange={(e) => set("label", e.target.value)}
                  placeholder="e.g. Services"
                />
              </div>

              <div className="anb-form-field anb-form-field--full">
                <label>Link / URL <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.link}
                  onChange={(e) => set("link", e.target.value)}
                  placeholder="e.g. /services or https://..."
                />
                <p className="anb-form-hint">
                  Use <code>/page-name</code> for internal links or a full URL for external.
                </p>
              </div>
            </div>
          </div>

          {/* ── Section: Hierarchy ── */}
          <div className="anb-form-section">
            <h3 className="anb-form-section__title">Hierarchy</h3>
            <div className="anb-form-grid">
              <div className="anb-form-field anb-form-field--full">
                <label>Parent Item</label>
                <select
                  value={form.parent_id}
                  onChange={(e) => set("parent_id", e.target.value)}
                >
                  <option value="">— Top-level (no parent) —</option>
                  {parentOptions.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <p className="anb-form-hint">
                  Leave blank to create a top-level item. Select a parent to nest this as a dropdown child.
                </p>
              </div>
            </div>

            {isChild && (
              <div className="anb-info-box">
                ℹ This item will appear as a dropdown child under the selected parent.
                Only top-level items can have children.
              </div>
            )}
          </div>

          {/* ── Section: Display Options ── */}
          <div className="anb-form-section">
            <h3 className="anb-form-section__title">Display Options</h3>
            <div className="anb-form-grid">
              <div className="anb-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                />
                <p className="anb-form-hint">Lower numbers appear first.</p>
              </div>

              <div className="anb-form-field anb-form-field--check-col">
                <label className="anb-form-label--block">Visibility</label>
                <label className="anb-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="anb-modal__footer">
          <button className="anb-btn anb-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="anb-btn anb-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Item" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ item, childCount, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/navbar/${item.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="anb-modal-overlay" onClick={onClose}>
      <div className="anb-modal anb-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="anb-delete__icon">🗑</div>
        <h2 className="anb-delete__title">Delete Nav Item?</h2>
        <p className="anb-delete__body">
          You are about to permanently delete{" "}
          <strong>{item?.label || "this item"}</strong>.
          This action cannot be undone.
        </p>
        {childCount > 0 && (
          <div className="anb-delete__warning">
            ⚠ This parent has <strong>{childCount} child item{childCount !== 1 ? "s" : ""}</strong>.
            Deleting it will also remove all its dropdown children.
          </div>
        )}
        {error && <div className="anb-modal__error">⚠ {error}</div>}
        <div className="anb-delete__actions">
          <button className="anb-btn anb-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="anb-btn anb-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   NAVBAR TREE ROW — Parent
   ═══════════════════════════════════ */
function ParentRow({ parent, children, expanded, onToggle, search, onView, onEdit, onDelete }) {
  return (
    <div className="anb-parent-block">
      {/* Parent row */}
      <div className="anb-row anb-row--parent">
        {/* Label */}
        <div className="anb-cell anb-label-cell">
          {children.length > 0 ? (
            <button className="anb-toggle" onClick={onToggle} title={expanded ? "Collapse" : "Expand"}>
              {expanded ? "−" : "+"}
            </button>
          ) : (
            <span className="anb-toggle-spacer" />
          )}
          <span className="anb-item-icon">🔗</span>
          <div className="anb-label-text">
            <span className="anb-label-text__main">
              {highlight(parent.label, search)}
            </span>
            <span className="anb-label-text__sub">
              {highlight(parent.link, search)}
            </span>
          </div>
          {children.length > 0 && (
            <span className="anb-child-count" title={`${children.length} children`}>
              {children.length}
            </span>
          )}
        </div>

        {/* Type */}
        <div className="anb-cell anb-cell--type">
          <span className="anb-type-badge anb-type-badge--parent">Parent</span>
        </div>

        {/* Order */}
        <div className="anb-cell anb-cell--order">
          <span className="anb-order-badge">{parent.order ?? 0}</span>
        </div>

        {/* Status */}
        <div className="anb-cell">
          <StatusBadge active={parent.active} />
        </div>

        {/* Children count (desktop) */}
        <div className="anb-cell">
          {children.length > 0
            ? <span style={{ fontSize: ".78rem", color: "#6b7280" }}>{children.length} child{children.length !== 1 ? "ren" : ""}</span>
            : <span style={{ color: "#ddd" }}>—</span>}
        </div>

        {/* Actions */}
        <div className="anb-cell">
          <DotMenu
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>

      {/* Children rows */}
      {expanded && children.map((child) => (
        <ChildRow
          key={child.id}
          child={child}
          search={search}
          onView={() => onView(child)}
          onEdit={() => onEdit(child)}
          onDelete={() => onDelete(child)}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════
   NAVBAR TREE ROW — Child
   ═══════════════════════════════════ */
function ChildRow({ child, search, onView, onEdit, onDelete }) {
  return (
    <div className="anb-row anb-row--child">
      {/* Label (indented) */}
      <div className="anb-cell anb-label-cell">
        <div className="anb-child-indent">
          <span className="anb-child-line" />
          <span className="anb-item-icon" style={{ fontSize: 12 }}>↳</span>
        </div>
        <div className="anb-label-text">
          <span className="anb-label-text__main">
            {highlight(child.label, search)}
          </span>
          <span className="anb-label-text__sub">
            {highlight(child.link, search)}
          </span>
        </div>
      </div>

      {/* Type */}
      <div className="anb-cell anb-cell--type">
        <span className="anb-type-badge anb-type-badge--child">Child</span>
      </div>

      {/* Order */}
      <div className="anb-cell anb-cell--order">
        <span className="anb-order-badge">{child.order ?? 0}</span>
      </div>

      {/* Status */}
      <div className="anb-cell">
        <StatusBadge active={child.active} />
      </div>

      {/* Children count placeholder */}
      <div className="anb-cell">
        <span style={{ color: "#ddd" }}>—</span>
      </div>

      {/* Actions */}
      <div className="anb-cell">
        <DotMenu
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminNavbar() {
  const [items,      setItems]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter,   setTypeFilter]   = useState("");  // "parent" | "child" | ""
  const [sort,         setSort]         = useState("order_asc");
  const searchTimer = useRef(null);

  /* Tree expand state — track which parent IDs are expanded */
  const [expanded, setExpanded] = useState({});

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load items ── */
  const loadItems = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    if (typeFilter)   q.set("type",   typeFilter);
    q.set("sort",  sort);
    q.set("limit", "200"); // load all for tree building

    apiFetch(`/api/admin/navbar?${q}`)
      .then((data) => {
        const rows = data.items || [];
        setItems(rows);
        setTotal(data.total || rows.length);
        /* Auto-expand all parents that have children */
        const expandMap = {};
        rows.forEach((item) => {
          if (!item.parent_id) {
            const hasKids = rows.some((i) => i.parent_id === item.id);
            if (hasKids) expandMap[item.id] = true;
          }
        });
        setExpanded((prev) => ({ ...expandMap, ...prev }));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, typeFilter, sort]);

  useEffect(() => { loadItems(); }, [loadItems]);

  /* ── Build tree structure ── */
  const parents  = items.filter((i) => !i.parent_id);
  const childMap = {};
  items.forEach((i) => {
    if (i.parent_id) {
      if (!childMap[i.parent_id]) childMap[i.parent_id] = [];
      childMap[i.parent_id].push(i);
    }
  });

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {}, 400);
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const openModal  = (type, item = null) => { setModal(type); setTarget(item); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadItems(); };

  /* Determine child count for delete warning */
  const targetChildCount = target && !target.parent_id
    ? (childMap[target.id] || []).length
    : 0;

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Navigation">

      {error && (
        <div className="anb-error-bar">⚠ {error}</div>
      )}

      {/* ── Toolbar ── */}
      <div className="anb-toolbar">
        <div className="anb-toolbar__left">
          <div className="anb-search-wrap">
            <span className="anb-search-wrap__icon">🔍</span>
            <input
              className="anb-search"
              type="text"
              placeholder="Search by label or link…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="anb-search-wrap__clear"
                onClick={() => setSearch("")}
              >✕</button>
            )}
          </div>

          <select
            className="anb-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            className="anb-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="parent">Parents only</option>
            <option value="child">Children only</option>
          </select>

          <select
            className="anb-filter-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="label_asc">Label A–Z</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>

        <div className="anb-toolbar__right">
          <span className="anb-toolbar__count">
            {loading ? "…" : `${total} item${total !== 1 ? "s" : ""}`}
          </span>
          <button className="anb-btn-primary" onClick={() => openModal("create")}>
            + Add Item
          </button>
        </div>
      </div>

      {/* ── Tree ── */}
      {loading ? (
        <div className="anb-tree-skeleton" />
      ) : items.length === 0 ? (
        <div className="anb-empty">
          <span>🔗</span>
          <p>No navigation items found.</p>
          {(search || statusFilter || typeFilter) && (
            <button
              className="anb-btn anb-btn--cancel"
              style={{ marginTop: 6 }}
              onClick={() => { setSearch(""); setStatusFilter(""); setTypeFilter(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="anb-tree">
          {/* Header */}
          <div className="anb-tree__header">
            <div className="anb-tree__header-cell">Label / Link</div>
            <div className="anb-tree__header-cell">Type</div>
            <div className="anb-tree__header-cell">Order</div>
            <div className="anb-tree__header-cell">Status</div>
            <div className="anb-tree__header-cell">Children</div>
            <div className="anb-tree__header-cell">Actions</div>
          </div>

          {parents.map((parent) => {
            const children = childMap[parent.id] || [];
            return (
              <ParentRow
                key={parent.id}
                parent={parent}
                children={children}
                expanded={!!expanded[parent.id]}
                onToggle={() => toggleExpand(parent.id)}
                search={search}
                onView={()  => openModal("view",   parent)}
                onEdit={()  => openModal("edit",   parent)}
                onDelete={() => openModal("delete", parent)}
              />
            );
          })}

          {/* Orphaned children (parent_id set but parent not in filtered list) */}
          {items
            .filter((i) => i.parent_id && !parents.find((p) => p.id === i.parent_id))
            .map((orphan) => (
              <div key={orphan.id} className="anb-parent-block">
                <ChildRow
                  child={orphan}
                  search={search}
                  onView={() => openModal("view",   orphan)}
                  onEdit={() => openModal("edit",   orphan)}
                  onDelete={() => openModal("delete", orphan)}
                />
              </div>
            ))}
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view" && (
        <ViewModal
          item={target}
          allItems={items}
          onClose={closeModal}
        />
      )}
      {modal === "create" && (
        <FormModal
          mode="create"
          allItems={items}
          onClose={closeModal}
          onSaved={afterSave}
        />
      )}
      {modal === "edit" && (
        <FormModal
          mode="edit"
          item={target}
          allItems={items}
          onClose={closeModal}
          onSaved={afterSave}
        />
      )}
      {modal === "delete" && (
        <DeleteModal
          item={target}
          childCount={targetChildCount}
          onClose={closeModal}
          onDeleted={afterSave}
        />
      )}

    </AdminLayout>
  );
}